// https://developer.zendesk.com/api-reference/widget-messaging/web/core/

// https://developer.zendesk.com/embeddables/docs/widget/core
// https://developer.zendesk.com/embeddables/docs/widget/settings

import { useAuth0 } from 'Auth';
import { useEffect, useState } from 'react';

type ZendeskVariant = 'main' | 'quoteApp';

const webWidgetDisplaySettings: {
  [k in ZendeskVariant]: {
    position: { horizontal: string; vertical: string };
    offset: { horizontal: string; vertical: string };
  };
} = {
  main: {
    position: { horizontal: 'left', vertical: 'bottom' },
    offset: {
      horizontal: '38px',
      vertical: '50px',
    },
  },
  quoteApp: {
    position: { horizontal: 'right', vertical: 'bottom' },
    offset: {
      horizontal: '20px',
      vertical: '167px',
    },
  },
};

export const useZendeskControls = (variant: ZendeskVariant = 'main') => {
  const { user } = useAuth0();
  const widgetIsMessengerVersion = window.zEmbed?.widget === 'messenger';
  const widgetName = widgetIsMessengerVersion ? 'messenger' : 'webWidget';

  const [isOpen, changeIsOpen] = useState<boolean>(false);

  const loaded = !!window.zEmbed;

  const hide = () => {
    if (!widgetIsMessengerVersion) {
      window.zE?.(widgetName, 'hide');
    }
  };

  const show = () => {
    if (!widgetIsMessengerVersion) {
      window.zE?.(widgetName, 'show');
    }
  };

  const close = () => {
    window.zE?.(widgetName, 'close');
    hide();
    changeIsOpen(false);
  };

  const open = () => {
    const webWidget = document.querySelector('#webWidget');
    if (webWidget) {
      webWidget.classList.add('fs-exclude');
    }
    show();
    window.zE?.(widgetName, 'open');
    if (!widgetIsMessengerVersion) {
      window.zE?.(widgetName, 'identify', {
        name: `${user?.firstName} ${user?.lastName}`,
        email: user?.email,
      });
    }

    changeIsOpen(true);
  };

  const toggleOpen = () => (isOpen ? close() : open());

  useEffect(() => {
    hide();
    if (!widgetIsMessengerVersion) {
      window.zE?.('webWidget:on', 'close', () => {
        changeIsOpen(false);
        hide();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    if (!widgetIsMessengerVersion) {
      const brandCobalt = '#4024D3'; //brand-cobalt
      window.zE?.(widgetName, 'updateSettings', {
        webWidget: {
          ...webWidgetDisplaySettings[variant],
          color: { header: brandCobalt, button: brandCobalt },
          chat: {
            departments: {
              select: 'IA Support',
            },
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  return { loaded, toggleOpen, isOpen, widgetIsMessengerVersion };
};
